/**
 * Driver-aware printer types for the shared printer service.
 *
 * Two **drivers** (the printer's language) and, under each, the **transports**
 * it can use:
 *
 *   star   → lan | bluetooth | ble | usb   (the existing Star integration)
 *   escpos → lan                            (raw TCP from the Electron shell;
 *                                            usb is the reserved next phase)
 *
 * A saved printer records both. `SavedStarPrinter` is exactly the record every
 * existing installation already has (`star_printers_v2`), with an optional
 * `driver` that older records simply lack — a missing driver means Star, so
 * nothing on disk has to change for an upgrade.
 */
import type {
  StarPrinterStatus,
  StarPrinterInterface,
  StarPrinterConnection,
} from '@am3nd-org/capacitor-starprint-plugin';

import type { EscPosProfileInput } from './escpos-profile';

export type PrinterDriver = 'star' | 'escpos';
export type PrinterTransport = StarPrinterInterface;

/** The persisted Star record. `driver` is absent on records saved before ESC/POS existed. */
export interface SavedStarPrinter {
  driver?: 'star';
  identifier: string;
  model: string;
  name?: string;
  interfaceType: StarPrinterInterface;
  connection: StarPrinterConnection;
  savedAt: string; // ISO 8601
  schemaVersion: 1 | 2;
}

/** How a generic ESC/POS printer is reached. Absent on records saved before attached printers existed (= LAN). */
export type EscPosTransport = 'lan' | 'windows-raw' | 'serial';

export interface EscPosUsbIdentity {
  vendorId: string;
  productId: string;
  serialNumber?: string;
}

/** The connection half of a saved LAN ESC/POS printer: an endpoint, nothing else. */
export interface EscPosLanConnection {
  interfaceType: 'lan';
  transport?: 'lan';
  identifier: string;
  ipAddress: string;
  /** Same as `ipAddress`; kept so code that reads Star's `address` keeps working. */
  address: string;
  port: number;
}

/**
 * An installed Windows print queue (the primary USB path). The queue name is
 * the key; driver, port, PnP id and USB identity let the shell find the same
 * device again after `USB001` becomes `USB002` or the queue is renamed.
 */
export interface EscPosWindowsRawConnection {
  interfaceType: 'usb';
  transport: 'windows-raw';
  identifier: string;
  queueName: string;
  driverName?: string;
  portName?: string;
  pnpDeviceId?: string;
  usb?: EscPosUsbIdentity;
}

/** A serial / virtual COM port. Identity beats the port number on reconnect. */
export interface EscPosSerialConnection {
  interfaceType: 'usb';
  transport: 'serial';
  identifier: string;
  portName: string;
  baudRate: number;
  friendlyName?: string;
  pnpDeviceId?: string;
  usb?: EscPosUsbIdentity;
}

export type EscPosConnection = EscPosLanConnection | EscPosWindowsRawConnection | EscPosSerialConnection;

export interface SavedEscPosPrinter {
  driver: 'escpos';
  /** `escpos:<ip>:<port>`, `escpos-raw:<queue>` or `escpos-serial:<COMx>` — stable across restarts. */
  identifier: string;
  /** Always `'ESC/POS'`: no model is queried or invented for a generic printer. */
  model: string;
  name?: string;
  /** `lan` for network printers; `usb` for Windows queues and COM ports (icons, labels). */
  interfaceType: 'lan' | 'usb';
  /** Absent on records saved before attached printers existed, which means `lan`. */
  transport?: EscPosTransport;
  connection: EscPosConnection;
  /** The operator's profile choices; main fills the defaults. */
  profile: EscPosProfileInput;
  savedAt: string;
  schemaVersion: 2;
}

export type SavedPrinter = SavedStarPrinter | SavedEscPosPrinter;

export function printerDriverOf(printer: Pick<SavedPrinter, 'driver'>): PrinterDriver {
  return printer.driver === 'escpos' ? 'escpos' : 'star';
}

export function isEscPosPrinter(printer: SavedPrinter): printer is SavedEscPosPrinter {
  return printer.driver === 'escpos';
}

export function isStarPrinter(printer: SavedPrinter): printer is SavedStarPrinter {
  return printer.driver !== 'escpos';
}

/** The transport of a saved ESC/POS printer; records from before attached printers are LAN. */
export function escPosTransportOf(printer: Pick<SavedEscPosPrinter, 'transport' | 'connection'>): EscPosTransport {
  return printer.transport ?? (printer.connection as { transport?: EscPosTransport }).transport ?? 'lan';
}

/** The identifier main assigns a LAN endpoint; the renderer computes the same one. */
export function escPosIdentifier(ipAddress: string, port: number): string {
  return `escpos:${ipAddress}:${String(port)}`;
}

export function escPosQueueIdentifier(queueName: string): string {
  return `escpos-raw:${queueName}`;
}

export function escPosSerialIdentifier(portName: string): string {
  return `escpos-serial:${portName.toUpperCase()}`;
}

function savedEscPosBase(identifier: string, name: string | undefined, profile: EscPosProfileInput | undefined) {
  return {
    driver: 'escpos' as const,
    identifier,
    model: 'ESC/POS',
    ...(name !== undefined ? { name } : {}),
    profile: profile ?? {},
    savedAt: new Date().toISOString(),
    schemaVersion: 2 as const,
  };
}

/** A new ESC/POS record for a network endpoint the operator chose. */
export function newEscPosPrinter(input: {
  ipAddress: string;
  port: number;
  name?: string;
  profile?: EscPosProfileInput;
}): SavedEscPosPrinter {
  const identifier = escPosIdentifier(input.ipAddress, input.port);
  return {
    ...savedEscPosBase(identifier, input.name, input.profile),
    interfaceType: 'lan',
    transport: 'lan',
    connection: {
      interfaceType: 'lan',
      transport: 'lan',
      identifier,
      ipAddress: input.ipAddress,
      address: input.ipAddress,
      port: input.port,
    },
  };
}

/** A new ESC/POS record for an installed Windows print queue (USB printer). */
export function newEscPosQueuePrinter(input: {
  queueName: string;
  driverName?: string;
  portName?: string;
  pnpDeviceId?: string;
  usb?: EscPosUsbIdentity;
  name?: string;
  profile?: EscPosProfileInput;
}): SavedEscPosPrinter {
  const identifier = escPosQueueIdentifier(input.queueName);
  return {
    ...savedEscPosBase(identifier, input.name, input.profile),
    interfaceType: 'usb',
    transport: 'windows-raw',
    connection: {
      interfaceType: 'usb',
      transport: 'windows-raw',
      identifier,
      queueName: input.queueName,
      ...(input.driverName !== undefined ? { driverName: input.driverName } : {}),
      ...(input.portName !== undefined ? { portName: input.portName } : {}),
      ...(input.pnpDeviceId !== undefined ? { pnpDeviceId: input.pnpDeviceId } : {}),
      ...(input.usb !== undefined ? { usb: input.usb } : {}),
    },
  };
}

export const DEFAULT_ESCPOS_SERIAL_BAUD_RATE = 9_600;

/** A new ESC/POS record for a serial / virtual COM port. */
export function newEscPosSerialPrinter(input: {
  portName: string;
  baudRate?: number;
  friendlyName?: string;
  pnpDeviceId?: string;
  usb?: EscPosUsbIdentity;
  name?: string;
  profile?: EscPosProfileInput;
}): SavedEscPosPrinter {
  const portName = input.portName.toUpperCase();
  const identifier = escPosSerialIdentifier(portName);
  return {
    ...savedEscPosBase(identifier, input.name, input.profile),
    interfaceType: 'usb',
    transport: 'serial',
    connection: {
      interfaceType: 'usb',
      transport: 'serial',
      identifier,
      portName,
      baudRate: input.baudRate ?? DEFAULT_ESCPOS_SERIAL_BAUD_RATE,
      ...(input.friendlyName !== undefined ? { friendlyName: input.friendlyName } : {}),
      ...(input.pnpDeviceId !== undefined ? { pnpDeviceId: input.pnpDeviceId } : {}),
      ...(input.usb !== undefined ? { usb: input.usb } : {}),
    },
  };
}

/**
 * How far the reported condition can be trusted: `realtime` (the printer
 * answers DLE EOT), `limited` (only what the Windows print queue says),
 * `unsupported` (queries off or never answered), `unknown` (not asked yet).
 */
export type StatusReporting = 'realtime' | 'limited' | 'unsupported' | 'unknown';

/**
 * The unified printer status. It is the Star plugin's shape — which every
 * consumer already reads — plus `statusReporting`, which says whether the
 * paper/cover flags mean anything. Absent means realtime (the Star SDK
 * always reports).
 */
export interface PrinterStatus extends StarPrinterStatus {
  statusReporting?: StatusReporting;
}

export type PrintJobOutcome =
  /** Every byte reached the printer; its own post-job condition, if reported, was clean. */
  | 'submitted'
  /** Nothing left this device. Safe to retry. */
  | 'not-sent'
  /** The write was interrupted or the printer reported a fault afterwards. Never auto-resent. */
  | 'delivery-unknown'
  /** The same job id was already accepted. */
  | 'duplicate';

export interface PrintJobError {
  code: string;
  message: string;
  recoverable?: boolean;
}

export interface PrintJobResult {
  success: boolean;
  outcome: PrintJobOutcome;
  /** The caller's `isCurrent` turned false before any hardware was touched; nothing printed. */
  cancelled?: boolean;
  /** Who took the bytes: the printer itself, or the operating system's print spooler. */
  acceptedBy?: 'printer' | 'spooler';
  jobId: string;
  printerId: string;
  status?: PrinterStatus;
  message?: string;
  error?: PrintJobError;
}

/** What a saved printer can do, stated explicitly rather than assumed. */
export interface PrinterCapabilities {
  driver: PrinterDriver;
  transports: readonly (PrinterTransport | EscPosTransport)[];
  statusReporting: StatusReporting;
  rasterImages: boolean;
  cutter: boolean;
  cashDrawer: boolean;
}

/** The method chosen in Settings; decides which setup/discovery flow is shown. */
export type PrinterMethod = PrinterDriver;

export interface PrinterStatusListener {
  (printerId: string | null, status: PrinterStatus): void;
}

export interface PrintImagesJobOptions {
  jobId: string;
  openCashDrawer?: boolean;
  /**
   * A customer-scoped job (self-checkout, KDS) may be cancelled while it waits
   * for the printer: rechecked before the bytes are handed to the driver and
   * reported as `cancelled`, never as a failure.
   */
  isCurrent?: () => boolean;
}

/** One driver's implementation behind the shared printer service. */
export interface PrinterAdapter {
  readonly driver: PrinterDriver;
  capabilities(printer: SavedPrinter): PrinterCapabilities;
  ensureConnected(printer: SavedPrinter): Promise<void>;
  disconnect(printer: SavedPrinter): Promise<void>;
  getStatus(printer: SavedPrinter, options: { forceReconnect?: boolean }): Promise<PrinterStatus>;
  printImages(
    printer: SavedPrinter,
    images: readonly string[],
    options: PrintImagesJobOptions
  ): Promise<PrintJobResult>;
  openCashDrawer(printer: SavedPrinter, options: { jobId: string }): Promise<PrintJobResult>;
  /**
   * An explicit test page on **this** printer, whether or not it is the active
   * one. Optional: a driver whose `printImages` already targets the printer it
   * is given (ESC/POS) has no need for it.
   */
  printTestImage?(printer: SavedPrinter, image: string, options: { jobId: string }): Promise<PrintJobResult>;
  /** Live status; `printerId` is null when the driver cannot name the printer (mobile SDK events). */
  subscribeStatus(listener: PrinterStatusListener): () => void;
}
